import { type PointerEvent as ReactPointerEvent, useEffect, useId, useRef, useState } from "react";
import { isTier, type Tier } from "../../src/tiers.ts";
import { api, type Item, type Person } from "./api.ts";
import { byTierOrder, myTier, RATINGS, saidWords } from "./format.ts";
import { ReportThis } from "./ReportThis.tsx";
import { useReadOnly } from "./readOnly.ts";
import { Avatar, AvatarStack, Icon } from "./souvenir.ts";
import { useRateLine } from "./Tips.tsx";
import { othersByTier, whoElse } from "./wants.ts";

// What each rating means, said when its button is held (a phone) or hovered (a computer).
export const MEANS: Record<string, string> = {
  no: "No: I'd rather skip it",
  maybe: "Maybe: I'd go if it fits",
  yes: "Yes: I really want this",
};

// How long a finger holds a button before it says what it means instead of rating.
const HOLD_MS = 450;
// How long the meaning stays after the finger lifts.
const SAID_MS = 2500;

// My own rating of something being considered, set right where I'm looking: a place's card, and every
// option, pick and suggestion on Ideas. One tap saves it as my change (Updates can undo it); tapping the
// one I have takes it back. Three buttons under "How much do you want this?", No, Maybe, Yes, left to
// right as everywhere they're offered, mine filled; held or hovered, one says what it means in the label's
// place. The first time someone sees them, a line says what they're for (Tips.tsx). None on a read-only
// trip, which takes no ratings.
export function RateMine(props: Parameters<typeof RateButtons>[0]) {
  return useReadOnly() ? null : <RateButtons {...props} />;
}

function RateButtons({
  item,
  trip,
  you,
  onChanged,
  onAnswer,
}: {
  item: Item;
  trip: string;
  you: string;
  onChanged: () => void;
  // Opened from the deck, its card in full: a rating given here answers the deck instead (it saves it, and
  // the card closes onto the next one). False when the deck can't take it (this isn't its card on top).
  onAnswer?: (tier: Tier) => boolean;
}) {
  const saved = myTier(item, you);
  // What I just tapped, shown at once while it saves; the trip's next load takes over.
  const [tapped, setTapped] = useState<{ tier: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The button saying what it means, held or hovered.
  const [saying, setSaying] = useState<string | null>(null);
  const hold = useRef<{ timer: number | null; held: boolean; lift: number | null }>({
    timer: null,
    held: false,
    lift: null,
  });
  const wrap = useRef<HTMLDivElement>(null);
  const line = useRateLine(item.id, wrap);
  const ids = useId();
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new load of the trip replaces what I tapped
  useEffect(() => setTapped(null), [saved]);
  useEffect(
    () => () => {
      const h = hold.current;
      if (h.timer !== null) clearTimeout(h.timer);
      if (h.lift !== null) clearTimeout(h.lift);
    },
    [],
  );
  const shown = tapped ? tapped.tier : saved;
  const rate = async (tier: string | null) => {
    line.rated();
    if (tier && onAnswer && isTier(tier) && onAnswer(tier)) {
      setTapped({ tier });
      return;
    }
    setTapped({ tier });
    setError(null);
    try {
      await api.rate(trip, item.id, tier);
      onChanged();
    } catch (e) {
      setTapped(null);
      setError(e instanceof Error ? e.message : "Couldn't save your rating. Try again.");
    }
  };
  // A finger held on a button: it says what it means, and lifting it doesn't rate.
  const press = (key: string, e: ReactPointerEvent) => {
    if (e.pointerType === "mouse") return;
    const h = hold.current;
    h.held = false;
    if (h.timer !== null) clearTimeout(h.timer);
    h.timer = window.setTimeout(() => {
      h.timer = null;
      h.held = true;
      if (h.lift !== null) clearTimeout(h.lift);
      h.lift = null;
      setSaying(key);
    }, HOLD_MS);
  };
  const lift = () => {
    const h = hold.current;
    if (h.timer !== null) clearTimeout(h.timer);
    h.timer = null;
    if (!h.held) return;
    if (h.lift !== null) clearTimeout(h.lift);
    h.lift = window.setTimeout(() => {
      h.lift = null;
      setSaying(null);
    }, SAID_MS);
  };
  return (
    <div className="tm-rate-wrap" ref={wrap} data-edit="rate">
      <p className={`caption tm-rate-label${saying ? " is-saying" : ""}`} aria-hidden="true">
        {saying ? MEANS[saying] : "How much do you want this?"}
      </p>
      <fieldset className="tm-rate" aria-label={`Your rating of ${item.title}`}>
        {RATINGS.map((r) => {
          const on = shown === r.key;
          return (
            <button
              key={r.key}
              type="button"
              className="tm-rate-option"
              aria-pressed={on}
              aria-describedby={`${ids}-${r.key}`}
              onClick={() => {
                // Held to hear what it means: not a rating.
                if (hold.current.held) {
                  hold.current.held = false;
                  return;
                }
                rate(on ? null : r.key);
              }}
              onPointerDown={(e) => press(r.key, e)}
              onPointerUp={lift}
              onPointerCancel={lift}
              onPointerEnter={(e) => e.pointerType === "mouse" && setSaying(r.key)}
              onPointerLeave={(e) => e.pointerType === "mouse" && setSaying((k) => (k === r.key ? null : k))}
              onFocus={(e) => e.currentTarget.matches(":focus-visible") && setSaying(r.key)}
              onBlur={() => setSaying((k) => (k === r.key ? null : k))}
              // A held finger is for the meaning: no menu or selection.
              onContextMenu={(e) => e.preventDefault()}
            >
              {r.label}
            </button>
          );
        })}
      </fieldset>
      {/* What each means, for screen readers: the buttons' descriptions. */}
      <div className="tm-hidden">
        {RATINGS.map((r) => (
          <span key={r.key} id={`${ids}-${r.key}`}>
            {shown === r.key ? `${MEANS[r.key]}. Your rating: tap to take it back.` : MEANS[r.key]}
          </span>
        ))}
      </div>
      {line.shown && (
        <p className="caption tm-rate-first" role="note">
          <Icon name="star" size={16} />
          <span>Rate it so your group, and your AI, know what matters to you.</span>
        </p>
      )}
      {error && (
        <p className="caption tm-error" role="alert">
          {error}
          <ReportThis />
        </p>
      )}
    </div>
  );
}

// Everyone else's rating of it, each with their face, Yes first: "Mei said yes · Ben said maybe", on the deck's
// card (DESIGN.md, "Rating one at a time"). Mine is on the strip. Past six others (or `byTier`, when even
// six don't fit on the card), one line per tier, Yes first: up to three faces then "+5", and "8 said yes",
// everyone in it named on hover and to a screen reader, as the who-else line does; so in a big group the
// card never cuts the list off.
export const NAMED_ON_CARD = 6;

export function Ratings({
  item,
  members,
  you,
  byTier = false,
}: {
  item: Item;
  members: Person[];
  you: string;
  byTier?: boolean;
}) {
  const rated = members.flatMap((m) => {
    if (m.id === you) return [];
    const tier = item.interest.find((x) => x.person === m.id)?.tier;
    return tier ? [{ m, tier }] : [];
  });
  rated.sort((a, b) => byTierOrder(a.tier, b.tier));
  if (rated.length === 0) return null;
  if (byTier || rated.length > NAMED_ON_CARD)
    return (
      <ul className="tm-ratings is-by-tier" aria-label="Their ratings">
        {othersByTier(item, members, you).map(({ tier, people, names }) => (
          <li key={tier} className="caption" title={`${names} ${saidWords(tier)}`}>
            {/* The words beside them say how many, and the hidden ones who. */}
            <span className="tm-ratings-faces" aria-hidden="true">
              <AvatarStack people={people} size={24} max={3} />
            </span>
            <span className="tm-ratings-count" aria-hidden="true">
              {people.length} <span className="tm-muted">{saidWords(tier)}</span>
            </span>
            <span className="tm-hidden">{`${names} ${saidWords(tier)}`}</span>
          </li>
        ))}
      </ul>
    );
  return (
    <ul className="tm-ratings" aria-label="Their ratings">
      {rated.map(({ m, tier }) => (
        <li key={m.id} className="caption">
          {/* The name beside it says who. */}
          <span className="tm-ratings-face" aria-hidden="true">
            <Avatar name={m.name} color={m.color} {...(m.src ? { src: m.src } : {})} size={20} />
          </span>
          <span>
            {m.name} <span className="tm-muted">{saidWords(tier)}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

// Who else wants it, on every suggestion on Ideas (features/recommendations.md): the faces of everyone else
// who said Yes or Maybe, overlapping (past three, "+2"), then the words, Yes first: "Yes from Mei · Maybe
// from Ben", past three people how many ("Yes from 8 · Maybe from 6"); or "Nobody else yet". Always there:
// mine is on the strip beside it.
export function WhoElse({ item, members, you }: { item: Item; members: Person[]; you: string }) {
  const { faces, text, all } = whoElse(item, members, you);
  return (
    // Past three people the words count them; hovered, they name everyone.
    <p className="caption tm-who-else" title={all === text ? undefined : all}>
      {faces.length > 0 && (
        // The words beside them say who.
        <span className="tm-who-else-faces" aria-hidden="true">
          <AvatarStack people={faces} size={24} max={3} />
        </span>
      )}
      {all === text ? (
        <span className={faces.length > 0 ? "tm-who-else-text" : "tm-who-else-text tm-muted"}>{text}</span>
      ) : (
        <>
          <span className="tm-who-else-text" aria-hidden="true">
            {text}
          </span>
          <span className="tm-hidden">{all}</span>
        </>
      )}
    </p>
  );
}
